import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classes from './PostContent.module.scss';
import { PostEditorContext } from '../states/PostEditorProvider';
import { UseFormReturn } from 'react-hook-form';
import { Toggle } from '../../../components/forms/Toggle';
import { CustomizeContentPerProfile } from '../CustomizeContentPerProfile';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { TextEditor } from '../TextEditor';
import { LinkBlock } from '../LinkBlock';
import { FirstComment } from '../FirstComment';
import { TitleBlock } from '../TitleBlock';
import { MediaBlock } from '../MediaBlock';
import { mapSelectedProfilesValues } from '../helpers/mapSelectedProfilesValues';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { PostStatusSelector } from '../PostStatusSelector';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { PostEditorContextEnum, PostEditorErrors, PostEditorPostErrors } from '../../../models/post-editor';
import { isArray } from 'lodash';
import { ErrorMessageSeverity } from '../../../constants/form';
import { InstagramPostTypeBlock } from '../InstagramPostTypeBlock';
import { GoogleButtonBlock } from '../GoogleButtonBlock';
import { AppContext } from '../../../providers/AppProvider';
import { useCustomizePerProfileWarning } from '../../../hooks/useCustomizePerProfileWarning';
import { Debugger } from '../../../components/containers/Debugger';
import { BeaconContext } from '../../../providers/BeaconProvider';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type PostContentViewProps = {
  //
  formInstance: UseFormReturn;
};

export const PostContentView = ({ formInstance }: PostContentViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postEditorContext } = useContext(AppContext);
  const { state, dispatch, profiles: socialProfiles } = useContext(PostEditorContext);
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const { customizeContentPerProfile, customizeSchedulePerProfile, activeCustomizeIndex, profiles } = postGroup;
  const stringifiedProfiles = JSON.stringify(profiles, undefined, '');
  const selectedProfiles = useMemo(() => {
    const parsedProfiles: SelectedSocialProfileValue[] = JSON.parse(stringifiedProfiles);
    return mapSelectedProfilesValues(parsedProfiles, socialProfiles);
  }, [stringifiedProfiles, socialProfiles]);
  const customizeIndex =
    typeof activeCustomizeIndex === 'number' && customizeContentPerProfile ? activeCustomizeIndex : undefined;
  const { toggleAction, warningModalElement } = useCustomizePerProfileWarning('content');

  const onTabChange = useCallback(
    (customizeIndex: number | null) => {
      dispatch({ type: 'setActiveCustomizeIndex', payload: { customizeIndex, postGroupIndex } });
    },
    [postGroupIndex, dispatch]
  );

  const [enableCustomizeProfile, setEnableCustomizeProfile] = useState<boolean>(
    selectedProfiles.length > 1 || postEditorContext.type === PostEditorContextEnum.edit
  );

  useEffect(() => {
    const parsedProfiles: SelectedSocialProfileValue[] = JSON.parse(stringifiedProfiles);
    setEnableCustomizeProfile(parsedProfiles.length > 1);
  }, [stringifiedProfiles]);

  let postGroupErrors: PostEditorErrors[] = [];
  postGroups.forEach(group => {
    postGroupErrors = [...postGroupErrors, group.errors];
  });

  const [_totalErrors, _totalWarnings] = postGroupErrors.reduce(
    (prev, curr) => {
      const [prevErr, prevWarn] = prev;
      const [newErr, newWarn] = Object.values<PostEditorPostErrors>(curr).reduce(
        (prevNumTuple, current) => {
          if (current) {
            if (isArray(current)) {
              const w = current.filter(e => e.severity === ErrorMessageSeverity.warning).length;
              const e = current.filter(er => er.severity === ErrorMessageSeverity.error).length;
              return [e, w];
            } else if (typeof current === 'object') {
              return current.severity === ErrorMessageSeverity.error
                ? [prevNumTuple[0] + 1, prevNumTuple[1]]
                : [prevNumTuple[0], prevNumTuple[1] + 1];
            }
          }
          return prevNumTuple;
        },
        [0, 0]
      );
      return [prevErr + newErr, prevWarn + newWarn];
    },
    [0, 0]
  );

  // const { errors: _, profiles: __, bitlyAccount: ___, ...rest } = postGroup;
  return (
    <>
      <div className={classes.PostContent}>
        {[PostEditorContextEnum.duplicate, PostEditorContextEnum.new, PostEditorContextEnum.import].includes(
          postEditorContext.type
        ) ? (
          <div
            className={classNames(
              'margin-bottom-tablet-portrait-24 grid-x align-middle align-justify',
              classes.PostContent
            )}>
            <div className={'cell shrink margin-bottom-small-16 margin-bottom-tablet-portrait-0'}>
              <div className={'grid-x align-middle'}>
                <div className={'cell shrink'}>
                  <Toggle
                    checked={customizeContentPerProfile}
                    disabled={state.loading || !enableCustomizeProfile}
                    formInstance={formInstance}
                    name={'customizeContentPerProfile'}
                    rules={{}}
                    action={toggleAction}
                    label={'Customize per Profile'}
                  />
                </div>
                {!state.loading && (
                  <div className={'cell shrink'}>
                    <Tooltip
                      target={`#tooltipTargetPostContent_${
                        customizeIndex !== undefined ? customizeIndex : 'group'
                      }_${postGroupIndex}`}
                      position={'right'}
                      pointer
                      type={'inlined'}
                      autoZIndex={false}
                      autoHide={false}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      <div>
                        When publishing to multiple profiles, you can either use the same content for all profiles, or
                        customize any part of the Post.{' '}
                        <a onClick={() => beacon('article', '667', { type: 'modal' })}>Learn more</a>
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
            {!customizeContentPerProfile && !customizeSchedulePerProfile ? (
              <div className={'cell shrink margin-bottom-small-16 margin-bottom-tablet-portrait-0'}>
                <PostStatusSelector formInstance={formInstance} customizeType={'global'} />
              </div>
            ) : null}
          </div>
        ) : null}
        {customizeContentPerProfile ? (
          <div className={'expand-margins'}>
            <CustomizeContentPerProfile
              onTabChange={onTabChange}
              activeIndex={activeCustomizeIndex}
              formInstance={formInstance}
            />
          </div>
        ) : (
          <>
            <Debugger tooltipContent={`Post Group`} modalContent={postGroup.groupPostAttributes} />
            <TextEditor />
            <FirstComment />
            <GoogleButtonBlock formInstance={formInstance} />
            <LinkBlock />
            <InstagramPostTypeBlock formInstance={formInstance} />
            <MediaBlock formInstance={formInstance} />
            <TitleBlock />
          </>
        )}
        {/*<pre>{JSON.stringify(postGroup.errors, undefined, 4)}</pre>*/}
        {/*<pre>{JSON.stringify(rest.groupPostAttributes.content.media, undefined, 4)}</pre>*/}
      </div>
      {warningModalElement}
    </>
  );
};
